import React from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { formatAmount } from '@/components/common/utils/money';
import { CompanyDTO, CustomerProfileDTO, LoanFormConfigDTO } from '@/shared/api/model';
import {
    DEFAULT_LOAN_APPLICATION_FORM_CONFIG,
    DEFAULT_LOAN_APPLICATION_TEXT_CONTENT,
    LoanApplicationTemplateConfig,
} from './LoanApplicationFormConfig';

const styles = StyleSheet.create({
    page: {
        paddingTop: 28,
        paddingBottom: 86,
        paddingHorizontal: 28,
        fontFamily: 'Helvetica',
        fontSize: 10,
        position: 'relative',
        color: '#111827',
    },
    watermark: {
        position: 'absolute',
        top: 175,
        left: 90,
        width: 420,
        height: 420,
        opacity: 0.07,
    },
    header: {
        alignItems: 'center',
        textAlign: 'center',
        borderBottomWidth: 2,
        borderBottomColor: '#111111',
        paddingBottom: 10,
        marginBottom: 12,
    },
    logo: {
        width: 74,
        height: 74,
        marginBottom: 6,
    },
    companyName: {
        fontSize: 13,
        fontWeight: 'bold',
        marginBottom: 2,
    },
    companyText: {
        fontSize: 8.7,
        marginBottom: 2,
    },
    titleWrapper: {
        borderWidth: 1,
        borderColor: '#111111',
        paddingHorizontal: 10,
        paddingVertical: 4,
        marginTop: 8,
        marginBottom: 4,
    },
    formTitle: {
        fontSize: 13,
        fontWeight: 'bold',
    },
    formSubtitle: {
        fontSize: 9,
        marginTop: 2,
    },
    metaRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 8,
        fontSize: 9,
        width: '100%',
    },
    metaText: {
        fontSize: 9,
        fontWeight: 'bold',
    },
    section: {
        marginBottom: 10,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        textDecoration: 'underline',
        marginBottom: 5,
    },
    twoColumnRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    column: {
        width: '48%',
    },
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 4,
    },
    fieldLabel: {
        fontSize: 9,
        fontWeight: 'bold',
        marginRight: 4,
    },
    fieldValue: {
        flex: 1,
        borderBottomWidth: 1,
        borderBottomColor: '#6B7280',
        borderBottomStyle: 'dotted',
        paddingBottom: 2,
        minHeight: 12,
        fontSize: 9,
    },
    summaryCard: {
        borderWidth: 1,
        borderColor: '#111111',
        backgroundColor: '#F9FAFB',
        padding: 8,
        marginTop: 4,
    },
    summaryTitle: {
        fontSize: 10,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 6,
    },
    summaryAmount: {
        fontSize: 13,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 6,
    },
    summaryLine: {
        fontSize: 9,
        marginBottom: 3,
    },
    paragraph: {
        fontSize: 9,
        lineHeight: 1.45,
        marginBottom: 4,
    },
    textArea: {
        minHeight: 28,
        borderBottomWidth: 1,
        borderBottomColor: '#6B7280',
        borderBottomStyle: 'dotted',
        marginTop: 4,
    },
    declarationBox: {
        borderWidth: 1,
        borderColor: '#111111',
        padding: 8,
        marginTop: 4,
    },
    signatureRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 10,
    },
    signatureBox: {
        width: '45%',
    },
    signatureLine: {
        borderTopWidth: 1,
        borderTopColor: '#111111',
        paddingTop: 4,
        marginTop: 24,
        fontSize: 9,
    },
    officeUseBox: {
        borderWidth: 1,
        borderColor: '#111111',
        padding: 8,
        marginTop: 4,
    },
    approvalHeaderRow: {
        flexDirection: 'row',
        backgroundColor: '#F3F4F6',
        borderBottomWidth: 1,
        borderBottomColor: '#111111',
        paddingVertical: 4,
        marginTop: 6,
    },
    approvalRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
        minHeight: 18,
        paddingVertical: 4,
    },
    approvalIndex: {
        width: '10%',
        fontSize: 9,
        paddingHorizontal: 4,
    },
    approvalName: {
        width: '45%',
        fontSize: 9,
        paddingHorizontal: 4,
    },
    approvalSignature: {
        width: '25%',
        fontSize: 9,
        paddingHorizontal: 4,
    },
    approvalDate: {
        width: '20%',
        fontSize: 9,
        paddingHorizontal: 4,
    },
    footer: {
        position: 'absolute',
        left: 28,
        right: 28,
        bottom: 22,
        textAlign: 'center',
        fontSize: 8,
        color: '#374151',
    },
});

interface ConfigurableLoanApplicationDocumentProps {
    user: any;
    product: any;
    amount: string | undefined;
    period: string | undefined;
    scheduleData: any;
    company: CompanyDTO | undefined;
    customerProfile?: CustomerProfileDTO;
    disbursementInstitutionName?: string;
    formConfig?: LoanFormConfigDTO;
}

const ConfigurableLoanApplicationDocument: React.FC<ConfigurableLoanApplicationDocumentProps> = ({
    user,
    product,
    amount,
    period,
    scheduleData,
    company,
    customerProfile,
    disbursementInstitutionName,
    formConfig,
}) => {
    const config = (formConfig || DEFAULT_LOAN_APPLICATION_FORM_CONFIG) as LoanApplicationTemplateConfig;
    const text = {
        ...DEFAULT_LOAN_APPLICATION_TEXT_CONTENT,
        ...(config.applicationForm?.textContent || {}),
    };
    const companyName = config.header?.companyName || company?.brFname || '';
    const companyAddress = config.header?.companyAddress || '';
    const companyPhone = config.header?.companyPhone || '';
    const companyEmail = config.header?.companyEmail || '';
    const formTitle = config.header?.formTitle || 'LOAN APPLICATION FORM';
    const logoUrl = config.branding?.logoUrl;
    const watermarkUrl = config.branding?.watermarkUrl || logoUrl;
    const showLogo = config.header?.showLogo ?? true;
    const showWatermark = Boolean(config.branding?.showWatermark && watermarkUrl);
    const scheduleItems = scheduleData?.scheduleDTO || [];
    const totalPrincipal = scheduleItems.reduce((sum: number, item: any) => sum + (item.principal || 0), 0) || Number(amount || 0);
    const totalInterest = scheduleData?.totalInterest || scheduleItems.reduce((sum: number, item: any) => sum + (item.interest || 0), 0);
    const totalRepayment = totalPrincipal + totalInterest;
    const firstInstallment = scheduleItems[0];
    const monthlyInstallment = firstInstallment
        ? (firstInstallment.totalDue || ((firstInstallment.principal || 0) + (firstInstallment.interest || 0)))
        : 0;
    const approvalRows = Number(config.applicationForm?.display?.approvalRows || 3);
    const isSwahili = config.language === 'SW';
    const currentDate = moment().format('DD/MM/YYYY');
    const applicationReference = `${text.referencePrefix}-${user?.custNo || 'APP'}-${Date.now().toString().slice(-6)}`;
    const requestedAmountText = amount ? `${formatAmount(Number(amount))} TZS` : '';
    const monthlyInstallmentText = monthlyInstallment ? `${formatAmount(monthlyInstallment)} TZS` : '';
    const totalPrincipalText = `${formatAmount(totalPrincipal)} TZS`;
    const totalInterestText = `${formatAmount(totalInterest)} TZS`;
    const totalRepaymentText = `${formatAmount(totalRepayment)} TZS`;
    const applicationDisplay = config.applicationForm?.display || {};
    const formatDate = (date?: string) => (date ? moment(date).format('DD/MM/YYYY') : '');
    const applicantFullName = customerProfile?.fullName || user?.fullname || '';
    const applicantCustomerNumber = customerProfile?.customerId || user?.custNo || '';
    const applicantDepartment = customerProfile?.employerName || '';
    const applicantOccupation = customerProfile?.employmentType || '';
    const applicantEmail = customerProfile?.email || user?.email || '';
    const applicantBranch = customerProfile?.branch || user?.branch || '';
    const applicantEmploymentDate = formatDate(customerProfile?.startDate);
    const applicantVoteNumber = customerProfile?.voteNumber || '';
    const applicantPhone = customerProfile?.phoneNumber || user?.phoneNo || '';
    const applicantRetirementDate = formatDate(customerProfile?.endDate);
    const disbursementAccountNumber = customerProfile?.disbursementAccRef || '';
    const disbursementBankName = disbursementInstitutionName || customerProfile?.disbursementFspCode || '';
    const labels = isSwahili
        ? {
            fullName: 'Jina kamili',
            checkNumber: 'Cheki namba',
            department: 'Idara/Wizara',
            vote: 'Voti',
            occupation: 'Kazi',
            phone: 'Simu',
            email: 'Baruapepe',
            branch: 'Tawi',
            employmentDate: 'Tarehe ya kuajiriwa',
            retirementDate: 'Tarehe ya kustaafu',
            product: 'Aina ya mkopo',
            amount: 'Ninaomba mkopo wa Tsh',
            period: 'Nitakaoulipa kwa muda wa',
            months: 'mwezi',
            accountNumber: 'Akaunti namba',
            bankName: 'Jina la benki',
        }
        : {
            fullName: 'Full Name',
            checkNumber: 'Customer Number',
            department: 'Department / Ministry',
            vote: 'Vote',
            occupation: 'Occupation',
            phone: 'Phone',
            email: 'Email',
            branch: 'Branch',
            employmentDate: 'Employment Date',
            retirementDate: 'Retirement Date',
            product: 'Product',
            amount: 'Requested Amount',
            period: 'Repayment Period',
            months: 'months',
            accountNumber: 'Account Number',
            bankName: 'Bank Name',
        };

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {showWatermark && watermarkUrl ? (
                    <Image src={watermarkUrl} style={styles.watermark} />
                ) : null}

                <View style={styles.header}>
                    {showLogo && logoUrl ? <Image src={logoUrl} style={styles.logo} /> : null}
                    <Text style={styles.companyName}>{companyName}</Text>
                    {companyAddress ? <Text style={styles.companyText}>{companyAddress}</Text> : null}
                    {companyPhone || companyEmail ? (
                        <Text style={styles.companyText}>
                            {companyPhone ? `Tel: ${companyPhone}` : ''}
                            {companyPhone && companyEmail ? '   ' : ''}
                            {companyEmail ? `Email: ${companyEmail}` : ''}
                        </Text>
                    ) : null}
                    <View style={styles.titleWrapper}>
                        <Text style={styles.formTitle}>{formTitle}</Text>
                    </View>
                    {text.headerSubtitle ? <Text style={styles.formSubtitle}>{text.headerSubtitle}</Text> : null}
                    <View style={styles.metaRow}>
                        <Text style={styles.metaText}>{text.applicationReferenceLabel}: {applicationReference}</Text>
                        <Text style={styles.metaText}>{text.applicationDateLabel}: {currentDate}</Text>
                    </View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{text.personalSectionTitle}</Text>
                    <View style={styles.twoColumnRow}>
                        <View style={styles.column}>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.fullName}:</Text>
                                <Text style={styles.fieldValue}>{applicantFullName}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.checkNumber}:</Text>
                                <Text style={styles.fieldValue}>{applicantCustomerNumber}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.department}:</Text>
                                <Text style={styles.fieldValue}>{applicantDepartment}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.occupation}:</Text>
                                <Text style={styles.fieldValue}>{applicantOccupation}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.email}:</Text>
                                <Text style={styles.fieldValue}>{applicantEmail}</Text>
                            </View>
                        </View>
                        <View style={styles.column}>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.branch}:</Text>
                                <Text style={styles.fieldValue}>{applicantBranch}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.employmentDate}:</Text>
                                <Text style={styles.fieldValue}>{applicantEmploymentDate}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.vote}:</Text>
                                <Text style={styles.fieldValue}>{applicantVoteNumber}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.phone}:</Text>
                                <Text style={styles.fieldValue}>{applicantPhone}</Text>
                            </View>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.retirementDate}:</Text>
                                <Text style={styles.fieldValue}>{applicantRetirementDate}</Text>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{text.loanSectionTitle}</Text>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{labels.product}:</Text>
                        <Text style={styles.fieldValue}>{product?.productName || ''}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{labels.amount}:</Text>
                        <Text style={styles.fieldValue}>{requestedAmountText}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{labels.period}:</Text>
                        <Text style={styles.fieldValue}>{period ? `${period} ${labels.months}` : ''}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{text.monthlyInstallmentLabel}:</Text>
                        <Text style={styles.fieldValue}>{monthlyInstallmentText}</Text>
                    </View>
                    {(applicationDisplay.showInterestRate ?? true) ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>{text.interestRateLabel}:</Text>
                            <Text style={styles.fieldValue}>{product?.loanRate || product?.baseInterestRate || '0'}%</Text>
                        </View>
                    ) : null}
                    {(applicationDisplay.showDisbursementDate ?? true) ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>{text.disbursementDateLabel}:</Text>
                            <Text style={styles.fieldValue}>{formatDate(scheduleData?.disbursementDate)}</Text>
                        </View>
                    ) : null}
                    {(applicationDisplay.showMaturityDate ?? true) ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>{text.maturityDateLabel}:</Text>
                            <Text style={styles.fieldValue}>{formatDate(scheduleData?.expireDate)}</Text>
                        </View>
                    ) : null}
                    {(applicationDisplay.showTotalPrincipal ?? true) ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>{text.totalPrincipalLabel}:</Text>
                            <Text style={styles.fieldValue}>{totalPrincipalText}</Text>
                        </View>
                    ) : null}
                    {(applicationDisplay.showTotalInterest ?? true) ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>{text.totalInterestLabel}:</Text>
                            <Text style={styles.fieldValue}>{totalInterestText}</Text>
                        </View>
                    ) : null}
                    {(applicationDisplay.showTotalRepayment ?? true) ? (
                        <View style={styles.fieldRow}>
                            <Text style={styles.fieldLabel}>{text.totalRepaymentLabel}:</Text>
                            <Text style={styles.fieldValue}>{totalRepaymentText}</Text>
                        </View>
                    ) : null}
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{text.purposeSectionTitle}</Text>
                    <Text style={styles.paragraph}>{text.purposePrompt}</Text>
                    <View style={styles.textArea}></View>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{text.collateralSectionTitle}</Text>
                    <Text style={styles.paragraph}>{text.collateralSummary}</Text>
                    <Text style={styles.paragraph}>{text.collateralDescription}</Text>
                    <Text style={styles.paragraph}>{text.collateralCommitment}</Text>
                </View>

                {(applicationDisplay.showPenaltySection ?? true) ? (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{text.penaltySectionTitle}</Text>
                        <Text style={styles.paragraph}>{text.penaltyText}</Text>
                    </View>
                ) : null}

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>{text.accountSectionTitle}</Text>
                    <View style={styles.twoColumnRow}>
                        <View style={styles.column}>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.accountNumber}:</Text>
                                <Text style={styles.fieldValue}>{disbursementAccountNumber}</Text>
                            </View>
                        </View>
                        <View style={styles.column}>
                            <View style={styles.fieldRow}>
                                <Text style={styles.fieldLabel}>{labels.bankName}:</Text>
                                <Text style={styles.fieldValue}>{disbursementBankName}</Text>
                            </View>
                        </View>
                    </View>
                    <Text style={styles.paragraph}>{text.accountPrompt}</Text>
                </View>

                {(applicationDisplay.showInsuranceSection ?? true) ? (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{text.insuranceSectionTitle}</Text>
                        <Text style={styles.paragraph}>{text.insuranceText}</Text>
                    </View>
                ) : null}

                <View style={styles.declarationBox}>
                    <Text style={styles.sectionTitle}>{text.applicantDeclarationTitle}</Text>
                    <Text style={styles.paragraph}>{text.applicantDeclarationText}</Text>
                    {(config.displayOptions?.includeSignatures ?? true) ? (
                        <View style={styles.signatureRow}>
                            <View style={styles.signatureBox}>
                                <View style={styles.signatureLine}>
                                    <Text>{text.applicantSignatureLabel}</Text>
                                </View>
                            </View>
                            <View style={styles.signatureBox}>
                                <View style={styles.signatureLine}>
                                    <Text>{text.signatureDateLabel}</Text>
                                </View>
                            </View>
                        </View>
                    ) : null}
                </View>

                <View style={styles.officeUseBox}>
                    <Text style={styles.sectionTitle}>{text.officeUseSectionTitle}</Text>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{text.officeUseApprovedDateLabel}:</Text>
                        <Text style={styles.fieldValue}></Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{text.officeUseApprovedAmountLabel}:</Text>
                        <Text style={styles.fieldValue}></Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{text.officeUseApprovedPeriodLabel}:</Text>
                        <Text style={styles.fieldValue}></Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>{text.officeUseInterestLabel}:</Text>
                        <Text style={styles.fieldValue}></Text>
                    </View>
                    <Text style={styles.paragraph}>{text.officeUseApprovalListTitle}</Text>
                    <View style={styles.approvalHeaderRow}>
                        <Text style={styles.approvalIndex}>#</Text>
                        <Text style={styles.approvalName}>{text.approvalTableNameLabel}</Text>
                        <Text style={styles.approvalSignature}>{text.approvalTableSignatureLabel}</Text>
                        <Text style={styles.approvalDate}>{text.approvalTableDateLabel}</Text>
                    </View>
                    {Array.from({ length: approvalRows }).map((_, index) => (
                        <View key={index} style={styles.approvalRow}>
                            <Text style={styles.approvalIndex}>{index + 1}</Text>
                            <Text style={styles.approvalName}></Text>
                            <Text style={styles.approvalSignature}></Text>
                            <Text style={styles.approvalDate}></Text>
                        </View>
                    ))}
                </View>

                <View style={styles.footer}>
                    <Text>{text.footerPrimary}</Text>
                    <Text style={{ marginTop: 2 }}>{text.footerSecondary}</Text>
                    <Text style={{ marginTop: 4 }}>Generated on: {moment().format('DD/MM/YYYY HH:mm')} | Ref: {applicationReference}</Text>
                </View>
            </Page>
        </Document>
    );
};

export default ConfigurableLoanApplicationDocument;
